import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

export const quizAnswerSchema = z.object({
  q: z.number().int().min(0).max(7),
  value: z.number().int().min(0).max(3),
});

export const quizDataSchema = z.object({
  answers: z.array(quizAnswerSchema).min(1),
  score: z.number().int().min(0).max(24),
  band: z.string().trim().min(1),
});

// Validation schema for lead submission
export const leadSchema = z.object({
  fullName: z.string().trim().min(1, "Full name is required").max(100, "Name must be less than 100 characters"),
  companyName: z.string().trim().min(1, "Company name is required").max(100, "Company name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(30, "Phone must be less than 30 characters").optional().or(z.literal("")),
  country: z.string().trim().max(100, "Country must be less than 100 characters").optional().or(z.literal("")),
  industry: z.string().trim().optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters"),
  pageSource: z.string().default("Home"),
  selectedPath: z.string().optional().nullable(),
  quiz: quizDataSchema.optional().nullable(),
});

export const blueprintAnswerSchema = z.object({
  challenge: z.string().trim().min(1, "Challenge description is required").max(500),
  timeline: z.string().trim().min(1, "Timeline is required"),
  aiMaturity: z.string().trim().min(1, "AI maturity is required"),
  blocker: z.string().trim().min(1, "Blocker description is required").max(500),
});

export type QuizData = z.infer<typeof quizDataSchema>;
export type LeadFormData = z.infer<typeof leadSchema>;
export type BlueprintAnswers = z.infer<typeof blueprintAnswerSchema>;

interface SubmitLeadResult {
  success: boolean;
  error?: string;
  rateLimited?: boolean;
  qaMode?: boolean;
  lead_id?: string;
  quiz_id?: string;
}

// Valid status values for leads
const VALID_STATUSES = ["New", "Contacted", "Closed"] as const;
export type LeadStatus = (typeof VALID_STATUSES)[number];

// Guard against rapid repeated clicks sending multiple network requests
let isSubmitting = false;

export const submitLead = async (data: LeadFormData, honeypot: string): Promise<SubmitLeadResult> => {
  if (isSubmitting) {
    return {
      success: false,
      error: "Submission in progress. Please wait.",
    };
  }

  isSubmitting = true;

  try {
    // Validate form data client-side first
    const validation = leadSchema.safeParse(data);
    if (!validation.success) {
      return {
        success: false,
        error: validation.error.errors[0]?.message || "Invalid form data",
      };
    }

    // Submit via edge function for proper rate limiting
    const response = await supabase.functions.invoke("submit-lead", {
      body: {
        fullName: validation.data.fullName,
        companyName: validation.data.companyName,
        email: validation.data.email,
        phone: validation.data.phone,
        country: validation.data.country,
        industry: validation.data.industry,
        message: validation.data.message,
        pageSource: validation.data.pageSource,
        selectedPath: validation.data.selectedPath || "",
        honeypot: honeypot,
        quiz: validation.data.quiz || undefined,
      },
    });

    if (response.error) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.error("Error submitting lead:", response.error.message);
      }
      return {
        success: false,
        error: "Failed to submit. Please try again.",
      };
    }

    const result = response.data;

    if (result.rateLimited) {
      return {
        success: false,
        error: result.error || "Too many submissions. Please try again later.",
        rateLimited: true,
      };
    }

    if (result.error) {
      return {
        success: false,
        error: result.error,
      };
    }

    return {
      success: true,
      qaMode: Boolean(result?.qaMode),
      lead_id: result?.lead_id,
      quiz_id: result?.quiz_id,
    };
  } catch (err) {
    // Only log in development
    if (import.meta.env.DEV) {
      console.error("Unexpected error submitting lead:", err);
    }
    return {
      success: false,
      error: "An unexpected error occurred. Please try again.",
    };
  } finally {
    isSubmitting = false;
  }
};

export interface ResilienceReport {
  success: boolean;
  summary?: string;
  recommendations?: string[];
  score?: number;
  band?: string;
  error?: string;
}

export const generateResilienceReport = async (quizId?: string, leadId?: string): Promise<ResilienceReport> => {
  if (!quizId && !leadId) {
    return { success: false, error: "Missing quiz or lead identifier" };
  }

  try {
    const response = await supabase.functions.invoke("generate-resilience-report", {
      body: {
        ...(quizId ? { quiz_id: quizId } : {}),
        ...(leadId ? { lead_id: leadId } : {}),
      },
    });

    if (response.error) {
      if (import.meta.env.DEV) {
        console.error("Error generating resilience report:", response.error.message);
      }
      return { success: false, error: "Failed to generate report. Please try again." };
    }

    const result = response.data;

    if (result.error) {
      return { success: false, error: result.error };
    }

    return {
      success: true,
      summary: result.summary,
      recommendations: result.recommendations,
      score: result.score,
      band: result.band,
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("Unexpected error generating report:", err);
    }
    return { success: false, error: "An unexpected error occurred. Please try again." };
  }
};

// Fetch leads for admin view
export const fetchLeads = async () => {
  const { data, error } = await supabase
    .from("leads")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    // Only log in development
    if (import.meta.env.DEV) {
      console.error("Error fetching leads:", error);
    }
    throw new Error("Failed to fetch leads");
  }

  return data;
};

// Update lead status with validation
export const updateLeadStatus = async (id: string, status: LeadStatus) => {
  // Validate status value
  if (!VALID_STATUSES.includes(status)) {
    throw new Error("Invalid status value");
  }

  const { error } = await supabase
    .from("leads")
    .update({ status })
    .eq("id", id);

  if (error) {
    // Only log in development
    if (import.meta.env.DEV) {
      console.error("Error updating lead status:", error);
    }
    throw new Error("Failed to update status");
  }
};

// Export leads to CSV
export const exportLeadsToCSV = (leads: any[]) => {
  if (!leads || leads.length === 0) return;

  const headers = [
    "Name",
    "Company",
    "Email",
    "Phone",
    "Country",
    "Industry",
    "Message",
    "Page Source",
    "Selected Path",
    "Status",
    "Date",
  ];

  const rows = leads.map((lead) => [
    lead.full_name,
    lead.company_name,
    lead.email,
    lead.phone || "",
    (lead as any).country || "",
    lead.industry,
    `"${(lead.message || "").replace(/"/g, '""')}"`,
    lead.page_source,
    lead.selected_path || "",
    lead.status,
    new Date(lead.created_at).toLocaleDateString(),
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `leads-${new Date().toISOString().split("T")[0]}.csv`);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export interface GenerateBlueprintResult {
  success: boolean;
  error?: string;
  rateLimited?: boolean;
  qaMode?: boolean;
  lead_id?: string;
  pdf_base64?: string;
  filename?: string;
}

// Guard against rapid repeated clicks on the blueprint generator
let isGeneratingBlueprint = false;

export const generateInnovationBlueprint = async (
  data: LeadFormData,
  blueprintAnswers: BlueprintAnswers,
  honeypot: string
): Promise<GenerateBlueprintResult> => {
  if (isGeneratingBlueprint) {
    return { success: false, error: "Generation in progress. Please wait." };
  }

  isGeneratingBlueprint = true;

  try {
    const validation = leadSchema.safeParse(data);
    if (!validation.success) {
      return {
        success: false,
        error: validation.error.errors[0]?.message || "Invalid form data",
      };
    }

    const answerValidation = blueprintAnswerSchema.safeParse(blueprintAnswers);
    if (!answerValidation.success) {
      return {
        success: false,
        error: answerValidation.error.errors[0]?.message || "Invalid blueprint answers",
      };
    }

    const response = await supabase.functions.invoke("generate-innovation-blueprint", {
      body: {
        fullName: validation.data.fullName,
        companyName: validation.data.companyName,
        email: validation.data.email,
        phone: validation.data.phone,
        country: validation.data.country,
        industry: validation.data.industry,
        message: validation.data.message,
        pageSource: validation.data.pageSource,
        selectedPath: validation.data.selectedPath || "",
        honeypot: honeypot,
        blueprintAnswers: {
          challenge: answerValidation.data.challenge,
          timeline: answerValidation.data.timeline,
          aiMaturity: answerValidation.data.aiMaturity,
          blocker: answerValidation.data.blocker,
        },
      },
    });

    if (response.error) {
      if (import.meta.env.DEV) {
        console.error("Error generating blueprint:", response.error.message);
      }
      return { success: false, error: "Failed to generate blueprint. Please try again." };
    }

    const result = response.data;

    if (result.rateLimited) {
      return {
        success: false,
        error: result.error || "Too many submissions. Please try again later.",
        rateLimited: true,
      };
    }

    if (result.error) {
      return { success: false, error: result.error };
    }

    return {
      success: true,
      qaMode: Boolean(result?.qaMode),
      lead_id: result?.lead_id,
      pdf_base64: result?.pdf_base64,
      filename: result?.filename,
    };
  } catch (err) {
    if (import.meta.env.DEV) {
      console.error("Unexpected error generating blueprint:", err);
    }
    return { success: false, error: "An unexpected error occurred. Please try again." };
  } finally {
    isGeneratingBlueprint = false;
  }
};
